/**
 * Green Flag — Icon registry
 *
 * Every icon is inlined as an SVG path (Font Awesome 6, Regular or Solid per
 * the brand spec in `README.md` §Iconography). We inline on purpose so:
 *   1. `currentColor` fill works natively — color flows from Chakra props
 *      (`color="fg.brand"`, etc.) without CSS filter hacks.
 *   2. No network requests per icon.
 *   3. The registry is the single source of truth — swap a path here, every
 *      consumer updates.
 *
 * ### Usage
 *
 * ```tsx
 * import { Icon } from "@green-flag/ui";
 * <Icon name="heart" boxSize="5" color="fg.onBrand" />
 * ```
 *
 * ### Adding an icon
 *
 * 1. Grab the Font Awesome 6 path from fontawesome.com (match Regular /
 *    Solid variant to existing icons — see the spec).
 * 2. Add `[name]: { viewBox, path }` to `ICON_REGISTRY`.
 * 3. Add the new name to the `IconName` type.
 *
 * Do NOT use `filter:` / `invert()` to recolor. Use `color`.
 */

import { chakra, type HTMLChakraProps } from "@chakra-ui/react";
import { forwardRef } from "react";

type IconRecord = {
  viewBox: string;
  /** Single `<path>` d-string. Ignored when `render` is provided. */
  path?: string;
  /** If true, render as a stroked (outline) icon instead of a filled shape. */
  stroke?: boolean;
  /** Stroke width in viewBox units. Only used when `stroke` is true. */
  strokeWidth?: number;
  /**
   * Escape hatch for multi-element glyphs (e.g. a broken heart where a
   * red heart outline is combined with a red slash and a white "gap" line).
   * Returns the SVG inner elements. `currentColor` still drives the main
   * tint; hardcode `var(--chakra-colors-bg-canvas)` for gap lines that
   * must match the button background.
   */
  render?: () => React.ReactNode;
};

// Font Awesome 6 paths (Regular for stroked glyphs, Solid for filled utility).
// Paths copied directly from Font Awesome 6 Free (CC BY 4.0), fontawesome.com.
const ICON_REGISTRY: Record<string, IconRecord> = {
  // ---- Brand mark (composite: outer circle + inner flag with fold detail) ----
  // Canonical SVG from brand; two subpaths — ring + flag w/ fold cutout.
  // viewBox 0 0 34 34 (authoritative).
  logo: {
    viewBox: "0 0 34 34",
    path:
      // outer ring
      "M32.2821 10.261C31.4357 8.26015 30.2247 6.46355 28.6827 4.92152C27.1406 3.37948 25.344 2.16744 23.3432 1.32204C21.269 0.444173 19.0691 0 16.801 0C14.533 0 12.3331 0.444173 10.261 1.321C8.26015 2.16744 6.46355 3.37844 4.92152 4.92047C3.37948 6.46251 2.16744 8.2591 1.32204 10.26C0.444173 12.3342 0 14.5341 0 16.801C0 19.068 0.444173 21.269 1.321 23.3411C2.16744 25.3419 3.37844 27.1385 4.92047 28.6806C6.46251 30.2226 8.2591 31.4347 10.26 32.2801C12.3331 33.1569 14.533 33.601 16.8 33.601C19.067 33.601 21.2679 33.1569 23.34 32.2801C25.3409 31.4336 27.1375 30.2226 28.6795 28.6806C30.2216 27.1385 31.4336 25.3419 32.279 23.3411C33.1558 21.2679 33.6 19.068 33.6 16.801C33.6 14.5341 33.1558 12.3331 32.279 10.261H32.2821ZM16.801 31.6127C8.62156 31.6127 1.9904 24.9816 1.9904 16.8021C1.9904 8.62261 8.62156 1.9904 16.801 1.9904C24.9805 1.9904 31.6117 8.62156 31.6117 16.801C31.6117 24.9805 24.9805 31.6117 16.801 31.6117V31.6127Z" +
      // flag on pole + internal fold cutout (evenodd-safe: the second subpath is a hole)
      "M26.376 15.3135C26.1215 15.2653 25.8721 15.2234 25.6281 15.1815L25.5903 15.3879L25.6249 15.1815C25.1305 15.0977 24.6632 15.0181 24.2222 14.9018C22.8583 14.5404 21.5959 13.7903 20.2487 12.5406C18.1913 10.6319 15.8039 9.88705 13.1472 10.326C13.1703 10.2181 13.1818 10.1081 13.1818 9.996C13.1818 9.1108 12.4621 8.39111 11.5769 8.39111C10.6917 8.39111 9.97202 9.1108 9.97202 9.996C9.97202 10.5324 10.2433 11.0331 10.6865 11.3296V25.7076H12.4673V19.485L12.5805 19.4483C13.0624 19.2922 13.5181 19.1445 13.979 18.9758C15.7672 18.3201 17.4894 18.3536 19.2452 19.0806C20.9862 19.8013 22.7493 19.7353 24.4841 18.8826C25.5097 18.3798 26.3425 17.6339 26.9595 16.667L27.6687 15.5565L26.3739 15.3125L26.376 15.3135ZM12.4705 12.3028C14.9931 11.581 17.1427 12.087 19.0388 13.8469C20.6081 15.303 22.1103 16.1851 23.7676 16.624C24.0169 16.69 24.2631 16.7435 24.4946 16.7895C24.2547 16.9792 23.9928 17.1426 23.7016 17.2861C22.4455 17.9031 21.1759 17.9534 19.9292 17.4369C18.801 16.9697 17.6445 16.733 16.4932 16.733C15.455 16.733 14.4043 16.9257 13.3683 17.306C13.076 17.4129 12.7743 17.5155 12.4789 17.6119C12.4789 17.2987 12.4789 16.9854 12.4789 16.6722V16.5329C12.4799 15.1469 12.481 13.716 12.4705 12.3038V12.3028Z",
  },

  // ---- UI chrome ----
  gear: {
    // Stroked gear.
    viewBox: "0 0 19 21",
    stroke: true,
    strokeWidth: 1.5,
    path:
      "M7.20761 1.64063C7.30768 1.12371 7.74465 0.75 8.25168 0.75H10.2464C10.7535 0.75 11.1904 1.12371 11.2905 1.64063L11.7742 4.08548C12.2445 4.29504 12.6848 4.56397 13.0851 4.8818L15.3467 4.09596C15.8271 3.92831 16.3541 4.13787 16.6076 4.5989L17.605 6.40809C17.8585 6.86912 17.7685 7.4489 17.3882 7.80165L15.6103 9.45717C15.6403 9.71562 15.6536 9.98107 15.6536 10.25C15.6536 10.5189 15.637 10.7844 15.6103 11.0428L17.3915 12.7018C17.7718 13.0546 17.8585 13.6379 17.6084 14.0954L16.611 15.9046C16.3575 16.3621 15.8304 16.5752 15.3501 16.4075L13.0885 15.6217C12.6848 15.9395 12.2445 16.205 11.7775 16.418L11.2972 18.8594C11.1938 19.3798 10.7568 19.75 10.2531 19.75H8.25836C7.75133 19.75 7.31435 19.3763 7.21428 18.8594L6.73393 16.418C6.2636 16.2085 5.82662 15.9395 5.423 15.6217L3.15138 16.4075C2.67103 16.5752 2.14399 16.3656 1.89048 15.9046L0.893097 14.0954C0.639582 13.6344 0.729647 13.0546 1.10992 12.7018L2.89119 11.0428C2.86117 10.7844 2.84783 10.5189 2.84783 10.25C2.84783 9.98107 2.8645 9.71562 2.89119 9.45717L1.10992 7.79816C0.729647 7.4454 0.642918 6.86213 0.893097 6.4046L1.89048 4.5954C2.14399 4.13438 2.67103 3.92482 3.15138 4.09246L5.41299 4.87831C5.81661 4.56048 6.25693 4.29504 6.72393 4.08199L7.20761 1.64063ZM9.24906 13.0441C10.7234 13.0371 11.9143 11.7833 11.9076 10.2395C11.901 8.69577 10.7034 7.4489 9.22905 7.45588C7.75466 7.46287 6.56381 8.71673 6.57048 10.2605C6.57715 11.8042 7.77468 13.0511 9.24906 13.0441Z",
  },
  search: {
    // FA6 Solid — magnifying-glass
    viewBox: "0 0 512 512",
    path:
      "M416 208c0 45.9-14.9 88.3-40 122.7L502.6 457.4c12.5 12.5 12.5 32.8 0 45.3s-32.8 12.5-45.3 0L330.7 376c-34.4 25.2-76.8 40-122.7 40C93.1 416 0 322.9 0 208S93.1 0 208 0S416 93.1 416 208zM208 352a144 144 0 1 0 0-288 144 144 0 1 0 0 288z",
  },
  sliders: {
    // FA6 Solid — sliders
    viewBox: "0 0 512 512",
    path:
      "M0 416c0 17.7 14.3 32 32 32l54.7 0c12.3 28.3 40.5 48 73.3 48s61-19.7 73.3-48L480 448c17.7 0 32-14.3 32-32s-14.3-32-32-32l-246.7 0c-12.3-28.3-40.5-48-73.3-48s-61 19.7-73.3 48L32 384c-17.7 0-32 14.3-32 32zm128 0a32 32 0 1 1 64 0 32 32 0 1 1 -64 0zM320 256a32 32 0 1 1 64 0 32 32 0 1 1 -64 0zm32-80c-32.8 0-61 19.7-73.3 48L32 224c-17.7 0-32 14.3-32 32s14.3 32 32 32l246.7 0c12.3 28.3 40.5 48 73.3 48s61-19.7 73.3-48l54.7 0c17.7 0 32-14.3 32-32s-14.3-32-32-32l-54.7 0c-12.3-28.3-40.5-48-73.3-48zM192 128a32 32 0 1 1 0-64 32 32 0 1 1 0 64zm73.3-64C253 35.7 224.8 16 192 16s-61 19.7-73.3 48L32 64C14.3 64 0 78.3 0 96s14.3 32 32 32l86.7 0c12.3 28.3 40.5 48 73.3 48s61-19.7 73.3-48L480 128c17.7 0 32-14.3 32-32s-14.3-32-32-32L265.3 64z",
  },
  "chevron-right": {
    // FA6 Solid — chevron-right
    viewBox: "0 0 320 512",
    path:
      "M310.6 233.4c12.5 12.5 12.5 32.8 0 45.3l-192 192c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3L242.7 256 73.4 86.6c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0l192 192z",
  },

  // ---- Mode / action glyphs ----
  heart: {
    // Outlined heart. Fill color is currentColor; set via `color` prop.
    viewBox: "0 0 34 30",
    path:
      "M24.8653 3.15C23.0738 3.15 21.3872 4.00969 20.3372 5.46L18.0797 8.58375C17.7844 8.99062 17.3119 9.23344 16.8066 9.23344C16.3013 9.23344 15.8288 8.99062 15.5334 8.58375L13.2759 5.46C12.2259 4.00969 10.5394 3.15 8.74781 3.15C5.66344 3.15 3.16313 5.65031 3.16313 8.73469C3.16313 12.0094 5.26313 15.1922 7.63219 18.0731C10.3294 21.3544 13.6303 24.2419 15.8944 25.9678C16.1044 26.1253 16.4128 26.2434 16.8131 26.2434C17.2134 26.2434 17.5219 26.1253 17.7319 25.9678C19.9959 24.2419 23.2969 21.3478 25.9941 18.0731C28.3697 15.1922 30.4631 12.0094 30.4631 8.73469C30.4631 5.65031 27.9628 3.15 24.8784 3.15H24.8653ZM17.7844 3.61594C19.425 1.34531 22.0631 0 24.8653 0C29.6888 0 33.6 3.91125 33.6 8.73469C33.6 13.2366 30.7847 17.1937 28.4091 20.0747C25.515 23.5922 22.0238 26.6437 19.6284 28.4681C18.8213 29.085 17.8238 29.3934 16.8 29.3934C15.7763 29.3934 14.7788 29.085 13.9716 28.4681C11.5763 26.6437 8.085 23.5922 5.19094 20.0812C2.81531 17.2003 0 13.2366 0 8.73469C0 3.91125 3.91125 0 8.73469 0C11.5369 0 14.175 1.34531 15.8156 3.61594L16.8 4.97437L17.7844 3.61594Z",
  },
  "heart-filled": {
    // FA6 Solid — heart
    viewBox: "0 0 512 512",
    path:
      "M47.6 300.4L228.3 469.1c7.5 7 17.4 10.9 27.7 10.9s20.2-3.9 27.7-10.9L464.4 300.4c30.4-28.3 47.6-68 47.6-109.5v-5.8c0-69.9-50.5-129.5-119.4-141C347 36.5 300.6 51.4 268 84L256 96 244 84c-32.6-32.6-79-47.5-124.6-39.9C50.5 55.6 0 115.2 0 185.1v5.8c0 41.5 17.2 81.2 47.6 109.5z",
  },
  "heart-broken": {
    // Broken heart: red outline + red diagonal slash + white "gap" line on
    // top to create the visual break. `currentColor` drives the red tint
    // so callers can set `color="fg.danger"` on the FAB; the gap line is
    // pinned to `bg.canvas` so it matches the white button background.
    viewBox: "0 0 36 31",
    render: () => (
      <>
        <line
          x1="3.12132"
          y1="1.5"
          x2="29"
          y2="27.3787"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
        />
        <path
          fill="currentColor"
          d="M26.8653 3.65C25.0738 3.65 23.3872 4.50969 22.3372 5.96L20.0797 9.08375C19.7844 9.49062 19.3119 9.73344 18.8066 9.73344C18.3013 9.73344 17.8288 9.49062 17.5334 9.08375L15.2759 5.96C14.2259 4.50969 12.5394 3.65 10.7478 3.65C7.66344 3.65 5.16313 6.15031 5.16313 9.23469C5.16313 12.5094 7.26313 15.6922 9.63219 18.5731C12.3294 21.8544 15.6303 24.7419 17.8944 26.4678C18.1044 26.6253 18.4128 26.7434 18.8131 26.7434C19.2134 26.7434 19.5219 26.6253 19.7319 26.4678C21.9959 24.7419 25.2969 21.8478 27.9941 18.5731C30.3697 15.6922 32.4631 12.5094 32.4631 9.23469C32.4631 6.15031 29.9628 3.65 26.8784 3.65H26.8653ZM19.7844 4.11594C21.425 1.84531 24.0631 0.5 26.8653 0.5C31.6888 0.5 35.6 4.41125 35.6 9.23469C35.6 13.7366 32.7847 17.6937 30.4091 20.5747C27.515 24.0922 24.0238 27.1437 21.6284 28.9681C20.8213 29.585 19.8238 29.8934 18.8 29.8934C17.7763 29.8934 16.7788 29.585 15.9716 28.9681C13.5763 27.1437 10.085 24.0922 7.19094 20.5812C4.81531 17.7003 2 13.7366 2 9.23469C2 4.41125 5.91125 0.5 10.7347 0.5C13.5369 0.5 16.175 1.84531 17.8156 4.11594L18.8 5.47437L19.7844 4.11594Z"
        />
        <line
          x1="1.41421"
          y1="1.5"
          x2="29"
          y2="29.0858"
          stroke="var(--chakra-colors-bg-canvas)"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </>
    ),
  },
  "thumbs-up": {
    viewBox: "0 0 14 14",
    path:
      "M4.73695 1.07053C5.31079 0.110453 6.53021 -0.275787 7.56478 0.209772L7.76342 0.314608C8.7235 0.891209 9.10974 2.11062 8.62418 3.14519L8.23518 3.97285H11.2616L11.4658 3.98388C12.4673 4.08596 13.248 4.93293 13.248 5.95922C13.248 6.32339 13.1487 6.65997 12.9776 6.95241C13.1459 7.24485 13.2453 7.58143 13.248 7.9456C13.248 8.45047 13.0577 8.90568 12.7514 9.25605C12.7873 9.40227 12.8066 9.55401 12.8066 9.71126C12.8066 10.4037 12.4507 11.0079 11.9182 11.3638C11.8658 12.3432 11.1071 13.135 10.1415 13.2316L9.93739 13.2426H7.06542C6.56883 13.2426 6.075 13.1488 5.61427 12.9695L5.41839 12.8867L5.39908 12.8784L5.21699 12.7901L5.19768 12.7819L4.8611 12.6025C4.52176 12.4232 4.21829 12.197 3.95344 11.9377C3.84032 12.6798 3.20027 13.2454 2.42779 13.2454H1.54496C0.692472 13.2454 0 12.5529 0 11.7004L0.00551773 5.51781C0.00551773 4.66532 0.69799 3.97285 1.55048 3.97285H2.43331C2.73127 3.97285 3.00991 4.05837 3.24717 4.20735L4.62936 1.26917L4.64591 1.23607L4.7204 1.09812L4.73695 1.07329V1.07053ZM1.55048 5.2971C1.42909 5.2971 1.32977 5.39642 1.32977 5.51781V11.6976C1.32977 11.819 1.42909 11.9183 1.55048 11.9183H2.43331C2.5547 11.9183 2.65402 11.819 2.65402 11.6976V5.51781C2.65402 5.39642 2.5547 5.2971 2.43331 5.2971H1.55048ZM7.00197 1.40711C6.59366 1.21675 6.11086 1.36297 5.87912 1.73818L5.81842 1.84853L4.25139 5.18399C4.15483 5.3909 4.09966 5.61161 4.08586 5.83783L4.0831 5.95371V9.06845L4.08862 9.2864C4.15483 10.1885 4.67902 10.9996 5.48736 11.43L5.80463 11.5983L5.97844 11.6838C6.32053 11.8383 6.69022 11.9183 7.06542 11.9183H9.93739L10.0036 11.9156C10.3374 11.8825 10.5995 11.5983 10.5995 11.2562L10.594 11.1845C10.5912 11.1597 10.5885 11.1376 10.583 11.1128C10.5085 10.7789 10.7016 10.4451 11.0244 10.3403C11.292 10.2548 11.4824 10.0037 11.4824 9.71126C11.4824 9.59263 11.452 9.48504 11.3968 9.38572C11.223 9.07949 11.3196 8.69049 11.6175 8.50289C11.8051 8.38426 11.9265 8.17734 11.9265 7.9456C11.9265 7.74972 11.841 7.57315 11.7003 7.449C11.5568 7.3221 11.4741 7.14277 11.4741 6.95241C11.4741 6.76205 11.5568 6.58272 11.7003 6.45582C11.841 6.33167 11.9265 6.1551 11.9265 5.95922L11.9238 5.89301C11.8934 5.58126 11.6451 5.33296 11.3334 5.30262L11.2672 5.29986H7.19785C6.97162 5.29986 6.76195 5.18399 6.64056 4.99362C6.51917 4.80326 6.50262 4.56048 6.59918 4.35633L7.42684 2.58239C7.61995 2.16856 7.46546 1.68024 7.08198 1.45126L7.00197 1.40711Z",
  },
  smile: {
    viewBox: "0 0 15 15",
    path:
      "M12.8011 7.06267C12.8011 3.89274 10.2326 1.32425 7.06267 1.32425C3.89274 1.32425 1.32425 3.89274 1.32425 7.06267C1.32425 10.2326 3.89274 12.8011 7.06267 12.8011C10.2326 12.8011 12.8011 10.2326 12.8011 7.06267ZM0 7.06267C0 3.16165 3.16165 0 7.06267 0C10.9637 0 14.1253 3.16165 14.1253 7.06267C14.1253 10.9637 10.9637 14.1253 7.06267 14.1253C3.16165 14.1253 0 10.9637 0 7.06267ZM4.89145 8.81178C5.30528 9.24216 6.02534 9.71117 7.06267 9.71117C8.1 9.71117 8.82006 9.24216 9.23389 8.81178C9.4877 8.54693 9.90705 8.53865 10.1691 8.79247C10.4312 9.04628 10.4423 9.46563 10.1884 9.72772C9.57874 10.3623 8.53314 11.0354 7.06267 11.0354C5.5922 11.0354 4.54659 10.3623 3.93688 9.72772C3.68307 9.46287 3.69135 9.04352 3.9562 8.79247C4.22105 8.54141 4.64039 8.54693 4.89145 8.81178ZM3.97275 5.73842C3.97275 5.2501 4.36727 4.85558 4.85558 4.85558C5.3439 4.85558 5.73842 5.2501 5.73842 5.73842C5.73842 6.22673 5.3439 6.62125 4.85558 6.62125C4.36727 6.62125 3.97275 6.22673 3.97275 5.73842ZM9.26975 4.85558C9.75807 4.85558 10.1526 5.2501 10.1526 5.73842C10.1526 6.22673 9.75807 6.62125 9.26975 6.62125C8.78143 6.62125 8.38692 6.22673 8.38692 5.73842C8.38692 5.2501 8.78143 4.85558 9.26975 4.85558Z",
  },
  "spark-filled": {
    // FA6 Solid — bolt. Used for the Sparks chip + bottom-nav spark glyph.
    viewBox: "0 0 448 512",
    path:
      "M349.4 44.6c5.9-13.7 1.5-29.7-10.6-38.5s-28.6-8-39.9 1.8l-256 224c-10 8.8-13.6 22.9-8.9 35.3S50.7 288 64 288H175.5L98.6 467.4c-5.9 13.7-1.5 29.7 10.6 38.5s28.6 8 39.9-1.8l256-224c10-8.8 13.6-22.9 8.9-35.3s-16.6-20.7-29.8-20.7H272.5L349.4 44.6z",
  },
  "file-lines": {
    // FA6 Regular — file-lines
    viewBox: "0 0 384 512",
    path:
      "M320 464c8.8 0 16-7.2 16-16V160H256c-17.7 0-32-14.3-32-32V48H64c-8.8 0-16 7.2-16 16V448c0 8.8 7.2 16 16 16H320zM0 64C0 28.7 28.7 0 64 0H229.5c17 0 33.3 6.7 45.3 18.7l90.5 90.5c12 12 18.7 28.3 18.7 45.3V448c0 35.3-28.7 64-64 64H64c-35.3 0-64-28.7-64-64V64zm112 192H272c8.8 0 16 7.2 16 16s-7.2 16-16 16H112c-8.8 0-16-7.2-16-16s7.2-16 16-16zm0 64H272c8.8 0 16 7.2 16 16s-7.2 16-16 16H112c-8.8 0-16-7.2-16-16s7.2-16 16-16zm0 64H272c8.8 0 16 7.2 16 16s-7.2 16-16 16H112c-8.8 0-16-7.2-16-16s7.2-16 16-16z",
  },

  chat: {
    // Stroked chat bubble with tail.
    viewBox: "0 0 22 20",
    stroke: true,
    strokeWidth: 1.5,
    path:
      "M4.05 0.9375C2.39435 0.9375 1.0125 2.19231 1.0125 3.75V12.5C1.0125 14.0577 2.39435 15.3125 4.05 15.3125H6.4125V19.0625L12.1875 15.3125H17.55C19.2057 15.3125 20.5875 14.0577 20.5875 12.5V3.75C20.5875 2.19231 19.2057 0.9375 17.55 0.9375H4.05Z",
  },
  "chat-slash": {
    // Stroked chat bubble with a diagonal slash through it — the "don't message" / pass affordance.
    viewBox: "0 0 22 22",
    stroke: true,
    strokeWidth: 1.5,
    path:
      "M4.05 1.9375C2.39435 1.9375 1.0125 3.19231 1.0125 4.75V13.5C1.0125 15.0577 2.39435 16.3125 4.05 16.3125H6.4125V20.0625L12.1875 16.3125H17.55C19.2057 16.3125 20.5875 15.0577 20.5875 13.5V4.75C20.5875 3.19231 19.2057 1.9375 17.55 1.9375H4.05Z M1.5 20.5 L20.5 1.5",
  },
  spark: {
    // Stroked lightning bolt.
    viewBox: "0 0 19 24",
    stroke: true,
    strokeWidth: 1.5,
    path:
      "M13.9848 0.998498C14.4496 1.34787 14.6215 1.98162 14.4106 2.53412L11.3481 10.5007H17.0004C17.5278 10.5007 17.9965 10.8419 18.1762 11.3579C18.3559 11.8738 18.2036 12.4507 17.8012 12.8001L6.55121 22.5501C6.1098 22.9319 5.48089 22.9522 5.01605 22.6029C4.55121 22.2535 4.37933 21.6197 4.59027 21.0672L7.65277 13.1007H2.00043C1.47308 13.1007 1.00433 12.7594 0.824645 12.2435C0.644957 11.7276 0.797301 11.1507 1.19964 10.8013L12.4496 1.05131C12.8911 0.669435 13.52 0.649123 13.9848 0.998498Z",
  },
  lock: {
    // FA6 Solid — lock
    viewBox: "0 0 448 512",
    path:
      "M144 144v48H304V144c0-44.2-35.8-80-80-80s-80 35.8-80 80zM80 192V144C80 64.5 144.5 0 224 0s144 64.5 144 144v48h16c35.3 0 64 28.7 64 64V448c0 35.3-28.7 64-64 64H64c-35.3 0-64-28.7-64-64V256c0-35.3 28.7-64 64-64H80z",
  },
  "chevron-left": {
    // FA6 Solid — chevron-left
    viewBox: "0 0 320 512",
    path:
      "M9.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l192 192c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L77.3 256 246.6 86.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-192 192z",
  },
  bell: {
    // FA6 Regular — bell
    viewBox: "0 0 448 512",
    path:
      "M224 0c-17.7 0-32 14.3-32 32V51.2C119 66 64 130.6 64 208v25.4c0 45.4-15.5 89.5-43.8 124.9L5.3 377c-5.8 7.2-6.9 17.1-2.9 25.4S14.8 416 24 416H424c9.2 0 17.6-5.3 21.6-13.6s2.9-18.2-2.9-25.4l-14.9-18.6C399.5 322.9 384 278.8 384 233.4V208c0-77.4-55-142-128-156.8V32c0-17.7-14.3-32-32-32zm0 96c61.9 0 112 50.1 112 112v25.4c0 47.9 13.9 94.6 39.7 134.6H72.3C98.1 328 112 281.3 112 233.4V208c0-61.9 50.1-112 112-112zm64 352H160c0 17 6.7 33.3 18.7 45.3s28.3 18.7 45.3 18.7s33.3-6.7 45.3-18.7s18.7-28.3 18.7-45.3z",
  },
  envelope: {
    // FA6 Regular — envelope
    viewBox: "0 0 512 512",
    path:
      "M64 112c-8.8 0-16 7.2-16 16v22.1L220.5 291.7c20.7 17 50.4 17 71.1 0L464 150.1V128c0-8.8-7.2-16-16-16H64zM48 212.2V384c0 8.8 7.2 16 16 16H448c8.8 0 16-7.2 16-16V212.2L322 328.8c-38.4 31.5-93.7 31.5-132 0L48 212.2zM0 128C0 92.7 28.7 64 64 64H448c35.3 0 64 28.7 64 64V384c0 35.3-28.7 64-64 64H64c-35.3 0-64-28.7-64-64V128z",
  },
  "credit-card": {
    // FA6 Regular — credit-card
    viewBox: "0 0 576 512",
    path:
      "M512 80c8.8 0 16 7.2 16 16v32H48V96c0-8.8 7.2-16 16-16H512zm16 144V416c0 8.8-7.2 16-16 16H64c-8.8 0-16-7.2-16-16V224H528zM64 32C28.7 32 0 60.7 0 96V416c0 35.3 28.7 64 64 64H512c35.3 0 64-28.7 64-64V96c0-35.3-28.7-64-64-64H64zm56 304c-13.3 0-24 10.7-24 24s10.7 24 24 24h48c13.3 0 24-10.7 24-24s-10.7-24-24-24H120zm128 0c-13.3 0-24 10.7-24 24s10.7 24 24 24H360c13.3 0 24-10.7 24-24s-10.7-24-24-24H248z",
  },
  user: {
    // FA6 Regular — user
    viewBox: "0 0 448 512",
    path:
      "M304 128a80 80 0 1 0 -160 0 80 80 0 1 0 160 0zM96 128a128 128 0 1 1 256 0A128 128 0 1 1 96 128zM49.3 464H398.7c-8.9-63.3-63.3-112-129-112H178.3c-65.7 0-120.1 48.7-129 112zM0 482.3C0 383.8 79.8 304 178.3 304h91.4C368.2 304 448 383.8 448 482.3c0 16.4-13.3 29.7-29.7 29.7H29.7C13.3 512 0 498.7 0 482.3z",
  },
  "user-solid": {
    // FA6 Solid — user
    viewBox: "0 0 448 512",
    path:
      "M224 256A128 128 0 1 0 224 0a128 128 0 1 0 0 256zm-45.7 48C79.8 304 0 383.8 0 482.3C0 498.7 13.3 512 29.7 512H418.3c16.4 0 29.7-13.3 29.7-29.7C448 383.8 368.2 304 269.7 304H178.3z",
  },
  x: {
    // FA6 Solid — xmark
    viewBox: "0 0 384 512",
    path:
      "M376.6 84.5c11.3-13.6 9.5-33.8-4.1-45.1s-33.8-9.5-45.1 4.1L192 206 56.6 43.5C45.3 29.9 25.1 28.1 11.5 39.4S-3.9 70.9 7.4 84.5L150.3 256 7.4 427.5c-11.3 13.6-9.5 33.8 4.1 45.1s33.8 9.5 45.1-4.1L192 306 327.4 468.5c11.3 13.6 31.5 15.4 45.1 4.1s15.4-31.5 4.1-45.1L233.7 256 376.6 84.5z",
  },
  check: {
    // FA6 Solid — check
    viewBox: "0 0 448 512",
    path:
      "M438.6 105.4c12.5 12.5 12.5 32.8 0 45.3l-256 256c-12.5 12.5-32.8 12.5-45.3 0l-128-128c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0L160 338.7 393.4 105.4c12.5-12.5 32.8-12.5 45.3 0z",
  },
  plus: {
    // FA6 Solid — plus
    viewBox: "0 0 448 512",
    path:
      "M256 80c0-17.7-14.3-32-32-32s-32 14.3-32 32V224H48c-17.7 0-32 14.3-32 32s14.3 32 32 32H192V432c0 17.7 14.3 32 32 32s32-14.3 32-32V288H400c17.7 0 32-14.3 32-32s-14.3-32-32-32H256V80z",
  },
  minus: {
    // FA6 Solid — minus
    viewBox: "0 0 448 512",
    path:
      "M432 256c0 17.7-14.3 32-32 32H48c-17.7 0-32-14.3-32-32s14.3-32 32-32H400c17.7 0 32 14.3 32 32z",
  },
  camera: {
    // FA6 Solid — camera
    viewBox: "0 0 512 512",
    path:
      "M149.1 64.8L138.7 96H64C28.7 96 0 124.7 0 160V416c0 35.3 28.7 64 64 64H448c35.3 0 64-28.7 64-64V160c0-35.3-28.7-64-64-64H373.3L362.9 64.8C356.4 45.2 338.1 32 317.4 32H194.6c-20.7 0-39 13.2-45.5 32.8zM256 192a96 96 0 1 1 0 192 96 96 0 1 1 0-192z",
  },
  cake: {
    // FA6 Solid — cake-candles
    viewBox: "0 0 448 512",
    path:
      "M86.4 5.5L61.8 47.6C58 54 56 61.3 56 68.7V80c0 22.1 17.9 40 40 40s40-17.9 40-40V68.7c0-7.4-2-14.8-5.8-21.1L105.6 5.5C103.6 2.1 100 0 96 0s-7.6 2.1-9.6 5.5zm128 0L189.8 47.6c-3.8 6.4-5.8 13.7-5.8 21.1V80c0 22.1 17.9 40 40 40s40-17.9 40-40V68.7c0-7.4-2-14.8-5.8-21.1L233.6 5.5C231.6 2.1 228 0 224 0s-7.6 2.1-9.6 5.5zM352 5.5L317.8 47.6c-3.8 6.4-5.8 13.7-5.8 21.1V80c0 22.1 17.9 40 40 40s40-17.9 40-40V68.7c0-7.4-2-14.8-5.8-21.1L361.6 5.5C359.6 2.1 356 0 352 0s-7.6 2.1-9.6 5.5zM128 176c-35.3 0-64 28.7-64 64v48h384V240c0-35.3-28.7-64-64-64H128zM32 320c-17.7 0-32 14.3-32 32v96c0 35.3 28.7 64 64 64H384c35.3 0 64-28.7 64-64V352c0-17.7-14.3-32-32-32H32z",
  },
  "pen-to-square": {
    // FA6 Regular — pen-to-square (standard "edit" glyph)
    viewBox: "0 0 512 512",
    path:
      "M441 58.9L453.1 71c9.4 9.4 9.4 24.6 0 33.9L424 134.1 377.9 88 407 58.9c9.4-9.4 24.6-9.4 33.9 0zM209.8 256.2L344 121.9 390.1 168 255.8 302.2c-2.9 2.9-6.5 5-10.4 6.1l-58.5 16.7 16.7-58.5c1.1-3.9 3.2-7.5 6.1-10.4zM373.1 25L175.8 222.2c-8.7 8.7-15 19.4-18.3 31.1l-28.6 100c-2.4 8.4-.1 17.4 6.1 23.6s15.2 8.5 23.6 6.1l100-28.6c11.8-3.4 22.5-9.7 31.1-18.3L487 138.9c28.1-28.1 28.1-73.7 0-101.8L474.9 25C446.8-3.1 401.2-3.1 373.1 25zM88 64C39.4 64 0 103.4 0 152L0 424c0 48.6 39.4 88 88 88l272 0c48.6 0 88-39.4 88-88l0-112c0-13.3-10.7-24-24-24s-24 10.7-24 24l0 112c0 22.1-17.9 40-40 40L88 464c-22.1 0-40-17.9-40-40l0-272c0-22.1 17.9-40 40-40l112 0c13.3 0 24-10.7 24-24s-10.7-24-24-24L88 64z",
  },
  "circle-check": {
    // FA6 Solid — circle-check
    viewBox: "0 0 512 512",
    path:
      "M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM369 209L241 337c-9.4 9.4-24.6 9.4-33.9 0l-64-64c-9.4-9.4-9.4-24.6 0-33.9s24.6-9.4 33.9 0l47 47L335 175c9.4-9.4 24.6-9.4 33.9 0s9.4 24.6 0 33.9z",
  },
  "ellipsis-vertical": {
    // FA6 Solid — ellipsis-vertical. Used for overflow/kebab menus.
    viewBox: "0 0 128 512",
    path:
      "M64 360a56 56 0 1 0 0 112 56 56 0 1 0 0-112zm0-160a56 56 0 1 0 0 112 56 56 0 1 0 0-112zM120 96A56 56 0 1 0 8 96a56 56 0 1 0 112 0z",
  },
  mobile: {
    // FA6 Solid — mobile-screen-button (cell phone).
    viewBox: "0 0 384 512",
    path:
      "M16 64C16 28.7 44.7 0 80 0H304c35.3 0 64 28.7 64 64V448c0 35.3-28.7 64-64 64H80c-35.3 0-64-28.7-64-64V64zM224 448c0-17.7-14.3-32-32-32s-32 14.3-32 32s14.3 32 32 32s32-14.3 32-32zM304 64H80V384H304V64z",
  },
  groupme: {
    // GroupMe brand mark (icons8), monochromed: outer rounded-square bubble
    // with the "GM" crosshair glyph and smile cut out via evenodd so the
    // tinted-circle background shows through the negative space.
    viewBox: "0 0 48 48",
    render: () => (
      <path
        fill="currentColor"
        fillRule="evenodd"
        clipRule="evenodd"
        d={
          // outer rounded-square chat bubble with speech tail
          "M34,42h-6l-4,5l-4-5h-6c-4.418,0-8-3.582-8-8V14c0-4.418,3.582-8,8-8h20c4.418,0,8,3.582,8,8v20C42,38.418,38.418,42,34,42z " +
          // hashmark glyph — 2 horizontals + 6 vertical segments broken around
          // the intersections so none of the sub-rects overlap (evenodd safe).
          "M17,15 h14 v3 h-14 z " +
          "M17,22 h14 v3 h-14 z " +
          "M19,13 h3 v2 h-3 z " +
          "M19,18 h3 v4 h-3 z " +
          "M19,25 h3 v2 h-3 z " +
          "M26,13 h3 v2 h-3 z " +
          "M26,18 h3 v4 h-3 z " +
          "M26,25 h3 v2 h-3 z " +
          // smile arc (same as original)
          "M24,36c-4.749,0-9.267-2.475-12.085-6.621c-0.466-0.685-0.288-1.618,0.396-2.084c0.686-0.463,1.618-0.288,2.084,0.397C16.688,31.065,20.189,33,24,33s7.312-1.935,9.604-5.307c0.467-0.687,1.399-0.861,2.084-0.397c0.685,0.466,0.862,1.399,0.396,2.084C33.267,33.525,28.749,36,24,36z"
        }
      />
    ),
  },
  instagram: {
    // Instagram brand mark (icons8) — monochrome silhouette; drives off `currentColor`.
    viewBox: "0 0 30 30",
    path:
      "M 9.9980469 3 C 6.1390469 3 3 6.1419531 3 10.001953 L 3 20.001953 C 3 23.860953 6.1419531 27 10.001953 27 L 20.001953 27 C 23.860953 27 27 23.858047 27 19.998047 L 27 9.9980469 C 27 6.1390469 23.858047 3 19.998047 3 L 9.9980469 3 z M 22 7 C 22.552 7 23 7.448 23 8 C 23 8.552 22.552 9 22 9 C 21.448 9 21 8.552 21 8 C 21 7.448 21.448 7 22 7 z M 15 9 C 18.309 9 21 11.691 21 15 C 21 18.309 18.309 21 15 21 C 11.691 21 9 18.309 9 15 C 9 11.691 11.691 9 15 9 z M 15 11 A 4 4 0 0 0 11 15 A 4 4 0 0 0 15 19 A 4 4 0 0 0 19 15 A 4 4 0 0 0 15 11 z",
  },

  calendar: {
    // FA6 Regular — calendar. The admin Events section.
    viewBox: "0 0 448 512",
    path:
      "M152 24c0-13.3-10.7-24-24-24s-24 10.7-24 24l0 40L64 64C28.7 64 0 92.7 0 128l0 16 0 48L0 448c0 35.3 28.7 64 64 64l320 0c35.3 0 64-28.7 64-64l0-256 0-48 0-16c0-35.3-28.7-64-64-64l-40 0 0-40c0-13.3-10.7-24-24-24s-24 10.7-24 24l0 40L152 64l0-40zM48 192l352 0 0 256c0 8.8-7.2 16-16 16L64 464c-8.8 0-16-7.2-16-16l0-256z",
  },
  "chart-bar": {
    // FA6 Regular — chart-bar. The admin Analytics section.
    viewBox: "0 0 512 512",
    path:
      "M24 32c13.3 0 24 10.7 24 24l0 352c0 13.3 10.7 24 24 24l416 0c13.3 0 24 10.7 24 24s-10.7 24-24 24L72 480c-39.8 0-72-32.2-72-72L0 56C0 42.7 10.7 32 24 32zM128 136c0-13.3 10.7-24 24-24l208 0c13.3 0 24 10.7 24 24s-10.7 24-24 24l-208 0c-13.3 0-24-10.7-24-24zm24 72l144 0c13.3 0 24 10.7 24 24s-10.7 24-24 24l-144 0c-13.3 0-24-10.7-24-24s10.7-24 24-24zm0 96l272 0c13.3 0 24 10.7 24 24s-10.7 24-24 24l-272 0c-13.3 0-24-10.7-24-24s10.7-24 24-24z",
  },
  "address-book": {
    // FA6 Regular — address-book. The admin Users section.
    viewBox: "0 0 512 512",
    path:
      "M384 48c8.8 0 16 7.2 16 16l0 384c0 8.8-7.2 16-16 16L96 464c-8.8 0-16-7.2-16-16L80 64c0-8.8 7.2-16 16-16l288 0zM96 0C60.7 0 32 28.7 32 64l0 384c0 35.3 28.7 64 64 64l288 0c35.3 0 64-28.7 64-64l0-384c0-35.3-28.7-64-64-64L96 0zM240 256a64 64 0 1 0 0-128 64 64 0 1 0 0 128zm-32 32c-44.2 0-80 35.8-80 80c0 8.8 7.2 16 16 16l192 0c8.8 0 16-7.2 16-16c0-44.2-35.8-80-80-80l-64 0zM512 80c0-8.8-7.2-16-16-16s-16 7.2-16 16l0 64c0 8.8 7.2 16 16 16s16-7.2 16-16l0-64zM496 192c-8.8 0-16 7.2-16 16l0 64c0 8.8 7.2 16 16 16s16-7.2 16-16l0-64c0-8.8-7.2-16-16-16zm16 144c0-8.8-7.2-16-16-16s-16 7.2-16 16l0 64c0 8.8 7.2 16 16 16s16-7.2 16-16l0-64z",
  },
  list: {
    // FA6 Solid — list. The admin events list view.
    viewBox: "0 0 512 512",
    path:
      "M40 48C26.7 48 16 58.7 16 72l0 48c0 13.3 10.7 24 24 24l48 0c13.3 0 24-10.7 24-24l0-48c0-13.3-10.7-24-24-24L40 48zM192 64c-17.7 0-32 14.3-32 32s14.3 32 32 32l288 0c17.7 0 32-14.3 32-32s-14.3-32-32-32L192 64zm0 160c-17.7 0-32 14.3-32 32s14.3 32 32 32l288 0c17.7 0 32-14.3 32-32s-14.3-32-32-32l-288 0zm0 160c-17.7 0-32 14.3-32 32s14.3 32 32 32l288 0c17.7 0 32-14.3 32-32s-14.3-32-32-32l-288 0zM16 232l0 48c0 13.3 10.7 24 24 24l48 0c13.3 0 24-10.7 24-24l0-48c0-13.3-10.7-24-24-24l-48 0c-13.3 0-24 10.7-24 24zM40 368c-13.3 0-24 10.7-24 24l0 48c0 13.3 10.7 24 24 24l48 0c13.3 0 24-10.7 24-24l0-48c0-13.3-10.7-24-24-24l-48 0z",
  },
  "chevron-down": {
    // FA6 Solid — chevron-down
    viewBox: "0 0 512 512",
    path:
      "M233.4 406.6c12.5 12.5 32.8 12.5 45.3 0l192-192c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L256 338.7 86.6 169.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l192 192z",
  },
  bars: {
    // FA6 Solid — bars. Opens the admin nav drawer on a phone.
    viewBox: "0 0 448 512",
    path:
      "M0 96C0 78.3 14.3 64 32 64l384 0c17.7 0 32 14.3 32 32s-14.3 32-32 32L32 128C14.3 128 0 113.7 0 96zM0 256c0-17.7 14.3-32 32-32l384 0c17.7 0 32 14.3 32 32s-14.3 32-32 32L32 288c-17.7 0-32-14.3-32-32zM448 416c0 17.7-14.3 32-32 32L32 448c-17.7 0-32-14.3-32-32s14.3-32 32-32l384 0c17.7 0 32 14.3 32 32z",
  },
  "trash-can": {
    // FA6 Regular — trash-can. Deletes an element from a marketing design.
    viewBox: "0 0 448 512",
    path:
      "M170.5 51.6L151.5 80l145 0-19-28.4c-1.5-2.2-4-3.6-6.7-3.6l-93.7 0c-2.7 0-5.2 1.3-6.7 3.6zm147-26.6L354.2 80 368 80l48 0 8 0c13.3 0 24 10.7 24 24s-10.7 24-24 24l-8 0 0 304c0 44.2-35.8 80-80 80l-224 0c-44.2 0-80-35.8-80-80l0-304-8 0c-13.3 0-24-10.7-24-24S10.7 80 24 80l8 0 48 0 13.8 0 36.7-55.1C140.9 9.4 158.4 0 177.1 0l93.7 0c18.7 0 36.2 9.4 46.6 24.9zM80 128l0 304c0 17.7 14.3 32 32 32l224 0c17.7 0 32-14.3 32-32l0-304L80 128zm80 64l0 208c0 8.8-7.2 16-16 16s-16-7.2-16-16l0-208c0-8.8 7.2-16 16-16s16 7.2 16 16zm80 0l0 208c0 8.8-7.2 16-16 16s-16-7.2-16-16l0-208c0-8.8 7.2-16 16-16s16 7.2 16 16zm80 0l0 208c0 8.8-7.2 16-16 16s-16-7.2-16-16l0-208c0-8.8 7.2-16 16-16s16 7.2 16 16z",
  },
  "rotate-left": {
    // FA6 Solid — rotate-left (Free has no Regular). Puts a moved element back.
    viewBox: "0 0 512 512",
    path:
      "M48.5 224L40 224c-13.3 0-24-10.7-24-24L16 72c0-9.7 5.8-18.5 14.8-22.2s19.3-1.7 26.2 5.2L98.6 96.6c87.6-86.5 228.7-86.2 315.8 1c87.5 87.5 87.5 229.3 0 316.8s-229.3 87.5-316.8 0c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0c62.5 62.5 163.8 62.5 226.3 0s62.5-163.8 0-226.3c-62.2-62.2-162.7-62.5-225.3-1L185 183c6.9 6.9 8.9 17.2 5.2 26.2s-12.5 14.8-22.2 14.8L48.5 224z",
  },
};

const ICON_NAME_LIST = [
  "logo",
  "gear",
  "search",
  "sliders",
  "chevron-right",
  "chevron-left",
  "heart",
  "heart-filled",
  "heart-broken",
  "thumbs-up",
  "smile",
  "spark-filled",
  "file-lines",
  "lock",
  "chat",
  "chat-slash",
  "spark",
  "bell",
  "envelope",
  "credit-card",
  "user",
  "user-solid",
  "x",
  "check",
  "plus",
  "minus",
  "camera",
  "cake",
  "circle-check",
  "pen-to-square",
  "ellipsis-vertical",
  "mobile",
  "groupme",
  "instagram",
  "calendar",
  "chart-bar",
  "address-book",
  "bars",
  "list",
  "chevron-down",
  "trash-can",
  "rotate-left",
] as const;

export type IconName = (typeof ICON_NAME_LIST)[number];

export const ICON_NAMES: readonly IconName[] = ICON_NAME_LIST;

// ---- <Icon /> component ----

const StyledSvg = chakra("svg", {
  base: {
    display: "inline-block",
    flexShrink: 0,
    color: "currentColor",
  },
});

export interface IconProps extends HTMLChakraProps<"svg"> {
  name: IconName;
  /** Shortcut for both width and height. Defaults to "1em" (scales with font-size). */
  boxSize?: string | number;
}

export const Icon = forwardRef<SVGSVGElement, IconProps>(function Icon(
  { name, boxSize = "1em", ...rest },
  ref
) {
  const entry = ICON_REGISTRY[name];
  if (!entry) {
    if (typeof console !== "undefined") {
      console.warn(`[Icon] Unknown icon name "${name}"`);
    }
    return null;
  }
  const stroked = entry.stroke === true;
  return (
    <StyledSvg
      ref={ref}
      as="svg"
      viewBox={entry.viewBox}
      width={boxSize}
      height={boxSize}
      aria-hidden="true"
      focusable="false"
      fill={stroked ? "none" : "currentColor"}
      {...rest}
    >
      {entry.render ? (
        entry.render()
      ) : (
        <path
          d={entry.path}
          {...(stroked && {
            stroke: "currentColor",
            strokeWidth: entry.strokeWidth ?? 1.5,
            strokeLinejoin: "round",
            vectorEffect: "non-scaling-stroke",
          })}
        />
      )}
    </StyledSvg>
  );
});
